import type { ExpenseKindEnum } from './expense'
import type { ModuleTypeEnum } from './module'
import type { SectionKey } from './navigation'

export enum PreferenceContextEnum {
  TABLE = 'table',
  THEME = 'theme',
  FILTERS = 'filters',
  LAYOUT = 'layout',
  OVERVIEW = 'overview',
  VIEWS = 'views',
  NOTIFICATIONS = 'notifications',
  TODAY = 'today',
  DOCUMENTS = 'documents',
  NAVIGATION = 'navigation',
  PROJECT_VIEW = 'project_view',
  FAVORITES = 'favorites',
  TASKS_PERSONAL = 'tasks_personal',
  PROJECT_TASK_COLUMNS = 'project_task_columns',
  THREAD_NAVIGATION = 'thread_navigation',
  EMOJI_REACTIONS = 'emoji_reactions',
  APP = 'app',
  ORG_PAGE = 'org_page',
  TABLE_MY_TASKS = 'table_my_tasks',
  // Workstream list table: widths/sort prefs (TABLE_WORKSTREAM) and column
  // visibility/order (WORKSTREAM_COLUMNS) — separate rows, mirroring how
  // My Tasks splits TABLE_MY_TASKS and TASKS_PERSONAL
  TABLE_WORKSTREAM = 'table_workstream',
  WORKSTREAM_COLUMNS = 'workstream_columns',
  DEFAULT_TAB = 'default_tab',
  TEAM_INSIGHTS = 'team_insights',
  // Per-user show/hide toggles for insight widgets on module overview pages
  OVERVIEW_WIDGETS = 'overview_widgets',
  // Personal accounts (Family Pulse): a member's self-set status for the space.
  // USER-scoped per org, payload = FamilyStatusPreference.
  FAMILY_STATUS = 'family_status',
}

export enum PreferenceScopeEnum {
  USER = 'user',
  ORGANIZATION = 'organization',
}

export interface UserPreferenceData {
  moduleType?: ModuleTypeEnum
  organizationId: string
  userId: string | null
  context: PreferenceContextEnum
  scope: PreferenceScopeEnum
  preferences: Record<string, unknown>
  isActive: boolean
  id?: string
  createdAt?: Date
  updatedAt?: Date
}

export interface ViewConfig {
  filters?: Record<string, unknown>
  columnFilters?: Record<string, ColumnFilterValue>
  columnVisibility?: Record<string, boolean>
  columnOrder?: Record<string, number>
  columnWidths?: Record<string, number>
  groupBy?: string | null
}

export interface SavedView {
  id: string
  name: string
  scope: PreferenceScopeEnum
  config: ViewConfig
  createdAt?: string
  updatedAt?: string
  /**
   * ScQL carried by this view, for surfaces that have a query bar. This is
   * what makes "this query + these columns + this grouping" one artifact
   * rather than two. Undefined for views that were never saved with a query.
   */
  query?: string | null
  /**
   * Where this view is stored.
   * - `record`: a first-class `saved_queries` row — has an owner, a
   *   visibility and a stable id, so it can be shared by id.
   * - `legacy`: still only inside the `UserPreference` views blob; promoted to
   *   a record the next time it is saved.
   * - `project`: project-scoped task view held in project settings.
   * Undefined on payloads written before views became first-class.
   */
  source?: 'record' | 'legacy' | 'project'
}

export interface ViewPreferences {
  views: SavedView[]
  activeViewId?: string
  defaultViewId?: string
}

// Column filter value type for persisted filters
export interface ColumnFilterValue {
  type: 'text' | 'number' | 'boolean' | 'select' | 'date' | 'user'
  value:
    | string
    | number
    | boolean
    | string[]
    | { min?: string | number; max?: string | number }
    | null
}

// Type for table-specific preferences
export interface TablePreferences {
  // Column width preferences - key is column ID, value is width in pixels
  columnWidths: Record<string, number>

  // Column visibility preferences - key is column ID, value is boolean for visibility
  columnVisibility?: Record<string, boolean>

  // Column order preferences - key is column ID with "_order" suffix, value is position
  columnOrder?: Record<string, number>

  // Sorting preferences - flat fields for easier use
  sortBy?: string | null
  sortOrder?: 'asc' | 'desc' | null

  // Page size preference
  pageSize?: number

  // Preferred view mode for modules using tables
  viewMode?: 'table' | 'card'

  // Column filters - persisted filter state for each column
  columnFilters?: Record<string, ColumnFilterValue>

  // Grouping preference - column ID to group by
  groupBy?: string | null
}

// Sidebar theme type - organization-level color scheme
export type SidebarTheme =
  | 'cast-iron-skillet' // #1A181B - Dark charcoal (default)
  | 'eggshell-indigo' // #2C59C3 - Deep blue
  | 'sunny-yolk' // #FFD957 - Bright yellow
  | 'free-range' // #F48A2F - Warm orange
  | 'herbed-frittata' // #1EAD5B - Fresh green
  | 'pickled-shell' // #F4A7C1 - Soft pink
  | 'purple-hen' // #A96BFF - Vibrant purple
  | 'paprika' // #D03B3C - Bold red
  | 'egg-white' // #FAFFFD - Clean white
  | 'fog' // #9A9A9E - Neutral gray

// Type for theme preferences
export interface ThemePreferences {
  /**
   * Deprecated for backend persistence. Light/dark/system is a local device
   * preference; synced theme records should only store color preferences.
   */
  mode?: 'light' | 'dark' | 'system'
  sidebarTheme?: SidebarTheme
  accentColor?: string
  fontSize?: 'small' | 'medium' | 'large'
  density?: 'compact' | 'comfortable' | 'spacious'
}

// Type for filter preferences
export interface FilterPreferences {
  lastSearch?: string
  activeFilters: {
    field: string
    operator: string
    value: unknown
  }[]
  savedFilters?: {
    name: string
    filters: {
      field: string
      operator: string
      value: unknown
    }[]
  }[]
}

// Type for layout preferences
export interface LayoutPreferences {
  sidebarCollapsed: boolean
  sidebarWidth?: number
  panelSizes?: Record<string, number>
  hiddenPanels?: string[]
}

// Type for document preferences
export interface DocumentPreferences {
  favoriteDocuments: string[] // Array of document IDs
  recentDocuments?: string[] // Optional: Track recently accessed documents
  defaultView?: 'grid' | 'list' // User's preferred view mode
  sortOrder?: {
    field: 'name' | 'createdAt' | 'updatedAt'
    direction: 'asc' | 'desc'
  }
}

// Type for today layout preferences
// small = 1/3 width, medium = 2/3 width, large = full row
export type TodayWidgetSize = 'small' | 'medium' | 'large'

export interface TodayLayoutItem {
  id: string
  size: TodayWidgetSize
}

export interface TodayLayoutData {
  items: TodayLayoutItem[]
}

/** Legacy two-column layout shape. Read-only — migrated to {items} on first save. */
export interface TodayLegacyLayoutData {
  main: string[]
  sidebar: string[]
}

export interface TodayLayoutPreferences {
  layout: TodayLayoutData
}

export type InfoPanelItemConfig =
  | InfoPanelFieldItemConfig
  | InfoPanelTableItemConfig
  | InfoPanelWidgetItemConfig

/** Background style options for info panel items */
export type InfoPanelItemBgStyle = 'default' | 'transparent' | 'subtle' | 'card'

/**
 * Modular card appearance for info panel items.
 *
 * Supersedes the legacy {bgStyle, showBorder} pair: when `appearance` is
 * present it wins; otherwise legacy fields are mapped forward at render time.
 * All properties are optional so a partial appearance inherits the surface
 * default (e.g. the org-page card shell).
 */
export type InfoPanelCardVariant =
  /** Elevated card shell (org overview default) */
  | 'card'
  /** No background, border, or shadow — content sits on the page */
  | 'transparent'
  /** Soft muted fill, borderless */
  | 'subtle'
  /** Border only, no fill or shadow */
  | 'outline'
  /** Tinted fill using the `tint` token */
  | 'tint'

/** Preset tint tokens — resolved to light/dark color pairs in CSS */
export type InfoPanelCardTint =
  | 'sand'
  | 'peach'
  | 'rose'
  | 'lilac'
  | 'sky'
  | 'mint'
  | 'lemon'
  | 'slate'

export type InfoPanelCardPadding = 'compact' | 'default' | 'spacious'

export type InfoPanelCardRadius = 'none' | 'default' | 'large'

export interface InfoPanelItemAppearance {
  variant?: InfoPanelCardVariant
  /** Tint token, used when variant is 'tint' */
  tint?: InfoPanelCardTint
  /** Override the variant's default border visibility */
  border?: boolean
  /** Override the variant's default shadow visibility */
  shadow?: boolean
  radius?: InfoPanelCardRadius
  /** Content density inside widget cards */
  padding?: InfoPanelCardPadding
  /** Hide the widget header (title + icon) */
  hideHeader?: boolean
}

export interface InfoPanelFieldItemConfig {
  type: 'field'
  fieldId: string
  /** Column span for grid layout (1-4, default 1) */
  colSpan?: 1 | 2 | 3 | 4
  /** Background style (legacy — superseded by appearance) */
  bgStyle?: InfoPanelItemBgStyle
  /** Show border around the item (legacy — superseded by appearance) */
  showBorder?: boolean
  /** Modular card appearance; wins over bgStyle/showBorder when present */
  appearance?: InfoPanelItemAppearance
}

export interface InfoPanelWidgetItemConfig {
  type: 'widget'
  widgetType: string // e.g., 'holidays', 'links', 'quick-links', 'embed'
  id: string // Unique instance ID
  title?: string // Optional title override
  settings?: Record<string, unknown> // Widget-specific config
  /** Column span for grid layout (1-4, default full width) */
  colSpan?: 1 | 2 | 3 | 4
  /** Background style (legacy — superseded by appearance) */
  bgStyle?: InfoPanelItemBgStyle
  /** Show border around the item (legacy — superseded by appearance) */
  showBorder?: boolean
  /** Modular card appearance; wins over bgStyle/showBorder when present */
  appearance?: InfoPanelItemAppearance
}

export interface InfoPanelTableItemConfig {
  type: 'table'
  id: string
  title: string
  source: InfoPanelTableSource
  columns?: string[]
  filters?: InfoPanelTableFilterConfig[]
  limit?: number
  /** Column span for grid layout (1-4, default full width) */
  colSpan?: 1 | 2 | 3 | 4
  /** Background style (legacy — superseded by appearance) */
  bgStyle?: InfoPanelItemBgStyle
  /** Show border around the item (legacy — superseded by appearance) */
  showBorder?: boolean
  /** Modular card appearance; wins over bgStyle/showBorder when present */
  appearance?: InfoPanelItemAppearance
}

export type InfoPanelTableSource =
  | { type: 'module'; moduleType: ModuleTypeEnum }
  | { type: 'collection'; collectionId: string }

export type InfoPanelTableFilterValueSource =
  | { type: 'field'; fieldId: string }
  | { type: 'static' }

export interface InfoPanelTableFilterConfig {
  key: string
  mode?: 'column' | 'query'
  value?: unknown
  valueSource?: InfoPanelTableFilterValueSource
}

export interface InfoPanelTabConfig {
  id: string
  label: string
  icon?: string
  items?: InfoPanelItemConfig[]
}

// Section width type for flexible layouts
export type InfoPanelSectionWidth = 'full' | 'half' | 'third' | 'two-thirds'

export interface InfoPanelSectionConfig {
  id: string
  title?: string
  layout?: 'list' | 'grid'
  gridColumns?: 2 | 3 | 4
  items?: InfoPanelItemConfig[]
  tabs?: InfoPanelTabConfig[]

  // Section width for flexible layouts (default: 'full')
  width?: InfoPanelSectionWidth

  // Row grouping - sections with same rowId display side-by-side
  rowId?: string
}

export interface InfoPanelPageTheme {
  /** Base page background in light mode. Accepts any CSS color value. */
  backgroundColor?: string
  /** Base page text color in light mode. Accepts any CSS color value. */
  textColor?: string
  /** Optional dark-mode page background. Falls back to backgroundColor. */
  darkBackgroundColor?: string
  /** Optional dark-mode page text color. Falls back to textColor. */
  darkTextColor?: string
}

export interface InfoPanelLayoutPreferences {
  version: number
  sections?: InfoPanelSectionConfig[]
  showUnassignedFields?: boolean
  theme?: InfoPanelPageTheme
  // Insight widget types that have already been seeded into this layout.
  // Prevents re-injecting a widget the org deliberately removed while still
  // letting newly shipped insight widgets appear once on existing layouts.
  seededWidgetTypes?: string[]
  // Tracks one-time default sizing migrations for generated insight sections
  // without overriding widths a user customizes afterward.
  insightLayoutVersion?: number
}

// Per-user visibility toggles for insight widgets on overview pages
// (stored at USER scope under PreferenceContextEnum.OVERVIEW_WIDGETS)
export interface OverviewWidgetPreferences {
  hiddenWidgetTypes: string[]
}

// Personal accounts (Family Pulse): a member's self-set status for the space.
// Stored at USER scope under PreferenceContextEnum.FAMILY_STATUS, one row per
// (user, org). `until` is an ISO timestamp after which the status is treated as
// cleared. All fields optional so an empty payload clears the status.
export interface FamilyStatusPreference {
  emoji?: string
  text?: string
  until?: string
}

// Family Pulse: one member's current status for the space-wide status endpoint.
// `status` is null when the member has never set one or their `until` expired.
export interface FamilyMemberStatus {
  memberId: string
  userId: string | null
  name: string | null
  image: string | null
  status: FamilyStatusPreference | null
}

export interface PinnedCollectionNavItem {
  collectionId: string
  slug: string
  name: string
  section: SectionKey
  icon?: string
}

/**
 * A module or page route the user pinned to their sidebar's Pinned section
 * ("Pin to sidebar" in a page's overflow menu). User-scoped, unlike
 * pinnedCollections which default to org scope.
 */
export interface PinnedNavRouteItem {
  /** Org-relative route, e.g. '/tasks' or '/knowledge-base/content' */
  href: string
  label: string
  /**
   * Record visuals captured at pin time (best effort — a later logo/color
   * change refreshes on re-pin). logoUrl wins over color; color renders as
   * an initial swatch; neither falls back to the module icon.
   */
  logoUrl?: string | null
  color?: string | null
}

// Type for navigation preferences - remembering last visited tabs/routes per module
export interface NavigationPreferences {
  // Key is module path (e.g., 'entities', 'timesheets'), value is last visited sub-route
  lastVisitedTabs: Record<string, string>
  // Store full paths for more complex routing scenarios
  lastVisitedPaths?: Record<string, string>
  // Collections pinned to the sidebar with section placement
  pinnedCollections?: PinnedCollectionNavItem[]
  // Module/page routes pinned to the sidebar's Pinned section (user-scoped)
  pinnedNavItems?: PinnedNavRouteItem[]
  // Per-user override of nav item visibility. Key = nav item href, value =
  // true when the item should be hidden (moved under "More"), false when it
  // should always be visible. When an href is absent the item's built-in
  // default (NavItem.hidden) applies.
  hiddenNavItems?: Record<string, boolean>
  // Index signature to make it compatible with Record<string, unknown>
  [key: string]: unknown
}

// Task view mode type - used for project tasks tab views
export type TaskViewMode = 'list' | 'board' | 'timeline' | 'calendar'

export interface ProjectTaskSectionExpansionPreferences {
  sections?: Record<string, boolean>
  // Per-section list-vs-grouped layout, keyed by the same section id used in
  // `sections` (e.g. `bucket:{id}`, `cycle:{id}`).
  layouts?: Record<string, 'list' | 'grouped'>
  [key: string]: unknown
}

export interface ProjectTaskColumnPreference {
  columnVisibility?: Record<string, boolean>
  columnOrders?: Record<string, number>
  columnWidths?: Record<string, number>
  lastUpdatedAt?: string
  [key: string]: unknown
}

export interface ProjectTaskColumnPreferences {
  byProject: Record<string, ProjectTaskColumnPreference>
  [key: string]: unknown
}

// Type for project view preferences
export interface ProjectViewPreferences {
  // Key is project ID, value is the preferred view mode
  taskViewMode: Record<string, TaskViewMode>
  // Default view mode when no preference exists for a project
  defaultTaskViewMode?: TaskViewMode
  // Main app task section expansion preferences, keyed by project ID
  taskSectionExpansion?: Record<string, ProjectTaskSectionExpansionPreferences>
  // Client portal task section expansion preferences, keyed by project ID
  clientPortalTaskSectionExpansion?: Record<
    string,
    ProjectTaskSectionExpansionPreferences
  >
  // Other project-specific preferences can be added here
  [key: string]: unknown
}

// Desktop notification settings (Tauri-specific)
export interface DesktopNotificationSettings {
  // Show badge count in dock/taskbar
  showBadgeCount?: boolean
  // Custom notification sound name (platform-specific)
  soundName?: string
  // Enable action buttons on notifications (View, Dismiss, etc.)
  enableActions?: boolean
  // Keep notifications visible until user interacts (don't auto-dismiss)
  persistNotifications?: boolean
  // Group notifications by type to reduce clutter
  groupNotifications?: boolean
}

// Type for browser notification preferences
export interface BrowserNotificationPreferences {
  // Whether browser notifications are enabled
  enabled: boolean
  // Whether the user intentionally disabled notifications
  userDisabled?: boolean
  // Whether permission has been requested from the browser
  permissionRequested: boolean
  // Current browser permission status
  permissionStatus?: 'granted' | 'denied' | 'default'
  // Show notifications when browser tab is active
  showWhenTabActive?: boolean
  // Show notifications when browser tab is inactive
  showWhenTabInactive?: boolean
  // Sound settings
  playSound?: boolean
  // Auto-dismiss time in milliseconds (0 = no auto-dismiss)
  autoDismissMs?: number
  // Which notification types to show as browser notifications
  enabledTypes?: {
    tasks?: boolean
    comments?: boolean
    mentions?: boolean
    assignments?: boolean
    deadlines?: boolean
    system?: boolean
  }
  // Desktop-specific settings (Tauri app)
  desktopSettings?: DesktopNotificationSettings
  // Index signature for compatibility
  [key: string]: unknown
}

// Type for a generic favorite module (any linkable type)
export interface FavoriteModule {
  id: string
  type: string
  name: string
  parentName?: string
  identifier?: string
}

// Type for favorites preferences
export interface FavoritesPreferences {
  // Array of favorite entity IDs
  favoriteEntities: string[]
  // Array of favorite project IDs
  favoriteProjects: string[]
  // Array of favorite task IDs (for future use)
  favoriteTasks?: string[]
  // Generic favorites for any module type (tasks, task groups, deals, etc.)
  favoriteModules?: FavoriteModule[]
  // Order for displaying favorites (most recent first by default)
  displayOrder?: 'recent' | 'alphabetical' | 'custom'
  // Maximum number of favorites to show in navigation
  maxNavigationDisplay?: number
  // Index signature for compatibility
  [key: string]: unknown
}

// Task list filter preferences (per project)
export interface TaskListFiltersPreferences {
  // Map of projectId -> filter settings
  byProject: Record<
    string,
    {
      excludeCompleted: boolean
      collapseCompletedRepeats?: boolean
      // Optional: persist other UI filters for restore-on-load
      search?: string
      assigneeIds?: string[]
      statuses?: string[]
      groupIds?: string[]
      cycleIds?: string[]
      taskTypeDefIds?: string[]
      dueDateFrom?: string
      dueDateTo?: string
      scheduledForFrom?: string
      scheduledForTo?: string
    }
  >
  // Index signature for compatibility with Record<string, unknown>
  [key: string]: unknown
}

export interface TicketListFilterSelection {
  type: string
  id: string
  name?: string
}

/** Filters shown in the ticket workspace's standard filter bar. */
export interface TicketListFilters {
  search: string
  stage: string
  stageTicketTypeId: string
  stageTypeId: string
  typeDefId: string
  priority: string
  assigneeIds: string[]
  followerIds: string[]
  client: TicketListFilterSelection | null
  source: string
  unread: boolean
  showSnoozed: boolean
  dueDateFrom: string
  dueDateTo: string
}

// Ticket workspace filter preferences (per user, persisted on the server).
// Filter fields are optional so preferences saved before they were introduced
// remain valid and hydrate with the current defaults.
export interface TicketListFiltersPreferences extends Partial<TicketListFilters> {
  hideClosed: boolean
  [key: string]: unknown
}

// Type guard functions
export function isTablePreferences(prefs: unknown): prefs is TablePreferences {
  return typeof prefs === 'object' && prefs !== null && 'columnWidths' in prefs
}

export function isThemePreferences(prefs: unknown): prefs is ThemePreferences {
  return (
    typeof prefs === 'object' &&
    prefs !== null &&
    ('mode' in prefs ||
      'sidebarTheme' in prefs ||
      'accentColor' in prefs ||
      'fontSize' in prefs ||
      'density' in prefs)
  )
}

export function isFilterPreferences(
  prefs: unknown,
): prefs is FilterPreferences {
  return typeof prefs === 'object' && prefs !== null && 'activeFilters' in prefs
}

export function isLayoutPreferences(
  prefs: unknown,
): prefs is LayoutPreferences {
  return (
    typeof prefs === 'object' && prefs !== null && 'sidebarCollapsed' in prefs
  )
}

export function isDocumentPreferences(
  prefs: unknown,
): prefs is DocumentPreferences {
  return (
    typeof prefs === 'object' && prefs !== null && 'favoriteDocuments' in prefs
  )
}

export function isTodayLayoutPreferences(
  prefs: unknown,
): prefs is TodayLayoutPreferences {
  return typeof prefs === 'object' && prefs !== null && 'layout' in prefs
}

/** Detects the legacy `{ main, sidebar }` payload shape so callers can migrate. */
export function isTodayLegacyLayoutData(
  data: unknown,
): data is TodayLegacyLayoutData {
  return (
    typeof data === 'object' &&
    data !== null &&
    Array.isArray((data as TodayLegacyLayoutData).main) &&
    Array.isArray((data as TodayLegacyLayoutData).sidebar)
  )
}

export function isTodayLayoutData(data: unknown): data is TodayLayoutData {
  return (
    typeof data === 'object' &&
    data !== null &&
    Array.isArray((data as TodayLayoutData).items)
  )
}

export function isInfoPanelLayoutPreferences(
  prefs: unknown,
): prefs is InfoPanelLayoutPreferences {
  if (typeof prefs !== 'object' || prefs === null) return false
  const value = prefs as {
    sections?: unknown
    showUnassignedFields?: unknown
    theme?: unknown
  }
  if (Array.isArray(value.sections)) return true
  if (typeof value.theme !== 'undefined') return true
  return typeof value.showUnassignedFields !== 'undefined'
}

// Type guards for InfoPanel item types
export function isInfoPanelFieldItem(
  item: InfoPanelItemConfig,
): item is InfoPanelFieldItemConfig {
  return item.type === 'field'
}

export function isInfoPanelTableItem(
  item: InfoPanelItemConfig,
): item is InfoPanelTableItemConfig {
  return item.type === 'table'
}

export function isInfoPanelWidgetItem(
  item: InfoPanelItemConfig,
): item is InfoPanelWidgetItemConfig {
  return item.type === 'widget'
}

export function isNavigationPreferences(
  prefs: unknown,
): prefs is NavigationPreferences {
  return (
    typeof prefs === 'object' && prefs !== null && 'lastVisitedTabs' in prefs
  )
}

export function isProjectViewPreferences(
  prefs: unknown,
): prefs is ProjectViewPreferences {
  return typeof prefs === 'object' && prefs !== null && 'taskViewMode' in prefs
}

export function isBrowserNotificationPreferences(
  prefs: unknown,
): prefs is BrowserNotificationPreferences {
  return (
    typeof prefs === 'object' &&
    prefs !== null &&
    'enabled' in prefs &&
    'permissionRequested' in prefs
  )
}

export function isFavoritesPreferences(
  prefs: unknown,
): prefs is FavoritesPreferences {
  return (
    typeof prefs === 'object' &&
    prefs !== null &&
    'favoriteEntities' in prefs &&
    'favoriteProjects' in prefs
  )
}

export function isTaskListFiltersPreferences(
  prefs: unknown,
): prefs is TaskListFiltersPreferences {
  return typeof prefs === 'object' && prefs !== null && 'byProject' in prefs
}

// Type for thread navigation preferences - remembering last opened thread
export interface ThreadNavigationPreferences {
  // Last opened thread ID
  lastOpenedThreadId?: string
  // Timestamp when the thread was last opened
  lastOpenedAt?: string
  // Last active tab (all, messages, or discussions)
  lastActiveTab?: 'all' | 'messages' | 'discussions'
  // Index signature for compatibility with Record<string, unknown>
  [key: string]: unknown
}

// Type for personal task organization preferences
export type PersonalTaskBucket =
  | 'today'
  | 'tomorrow'
  | 'thisWeek'
  | 'nextWeek'
  | 'later'
  | 'unscheduled'

/** @deprecated Use PersonalTaskBucket. Kept for direct-module compatibility. */
export type TaskBucket = PersonalTaskBucket

export interface TasksPersonalPreferences {
  version: number
  order: Record<PersonalTaskBucket, string[]>
  lastTouched: string
  viewMode?: 'list' | 'board' | 'deck'
  // Allow users to hide completed tasks
  hideCompleted?: boolean
  // Optional project grouping within buckets
  groupByProject?: boolean
  // Track which buckets are collapsed
  collapsedBuckets?: PersonalTaskBucket[]
  // Widget preferences for the My Tasks widget
  widget?: {
    showCalendarEvents?: boolean
  }
  // Store applied filters
  filters?: {
    search?: string
    entityIds?: string[]
    projectIds?: string[]
    statuses?: string[]
    dueDateFrom?: string
    dueDateTo?: string
    scheduledForFrom?: string
    scheduledForTo?: string
  }
  // Column preferences for My Tasks table
  columnVisibility?: Record<string, boolean>
  columnOrders?: Record<string, number>
  columnWidths?: Record<string, number>
  // Hide the column header row on the My Work / My Tasks list tables
  hideTableHeaders?: boolean
  // Index signature for compatibility
  [key: string]: unknown
}

export function isTasksPersonalPreferences(
  prefs: unknown,
): prefs is TasksPersonalPreferences {
  return (
    typeof prefs === 'object' &&
    prefs !== null &&
    'version' in prefs &&
    'order' in prefs
  )
}

export function isThreadNavigationPreferences(
  prefs: unknown,
): prefs is ThreadNavigationPreferences {
  return (
    typeof prefs === 'object' &&
    prefs !== null &&
    ('lastOpenedThreadId' in prefs || 'lastActiveTab' in prefs)
  )
}

// Type for emoji reaction preferences
export interface EmojiReactionsPreferences {
  /** Default quick-access emojis (unified codes like "1F44D") */
  defaultEmojis: string[]
  /** Recently used emojis for smart ordering (unified codes) */
  recentlyUsed?: string[]
  /** Index signature for compatibility */
  [key: string]: unknown
}

export function isEmojiReactionsPreferences(
  prefs: unknown,
): prefs is EmojiReactionsPreferences {
  return typeof prefs === 'object' && prefs !== null && 'defaultEmojis' in prefs
}

// Type for app-level preferences (deep linking, AI features, etc.)
export const SLACK_OPEN_TARGET_VALUES = ['auto', 'app', 'web'] as const
export type SlackOpenTarget = (typeof SLACK_OPEN_TARGET_VALUES)[number]
export const DEFAULT_SLACK_OPEN_TARGET: SlackOpenTarget = 'auto'

export function isSlackOpenTarget(value: unknown): value is SlackOpenTarget {
  return (
    typeof value === 'string' &&
    (SLACK_OPEN_TARGET_VALUES as readonly string[]).includes(value)
  )
}

export function resolveSlackOpenTargetPreference(
  prefs: AppPreferences | Record<string, unknown> | null | undefined,
): SlackOpenTarget {
  if (!prefs || typeof prefs !== 'object') {
    return DEFAULT_SLACK_OPEN_TARGET
  }

  const value = (prefs as Record<string, unknown>).slackOpenTarget
  return isSlackOpenTarget(value) ? value : DEFAULT_SLACK_OPEN_TARGET
}

export interface AppPreferences {
  /** Whether to prefer opening shared links in the desktop app (defaults to true) */
  preferDesktopApp: boolean
  /** Slack launcher preference for "Open in Scrambl" links */
  slackOpenTarget?: SlackOpenTarget
  /** Whether the Benny AI assistant is enabled (defaults to false — user must opt in) */
  bennyEnabled?: boolean
  /** Whether the desktop floating Benny companion is enabled (desktop only) */
  bennyCompanionEnabled?: boolean
  /**
   * Whether desktop notifications are delivered through the floating Benny
   * companion (speech bubbles) instead of native system notifications
   * (desktop only, requires the companion to be enabled)
   */
  bennyNotificationsEnabled?: boolean
  /**
   * Last view the user had open in the floating Benny companion hub, so it
   * reopens where they left off (including after an app relaunch).
   * Undefined means "never chosen" — the companion shows its launcher.
   */
  bennyCompanionView?: 'chat' | 'upcoming' | 'favorites'
  /** Index signature for compatibility */
  [key: string]: unknown
}

export interface ExpenseDraftPreferences extends Record<string, unknown> {
  lastUsedKind?: ExpenseKindEnum
  lastUsedCurrency?: string
  lastUsedPaymentMethod?: string
  lastUsedCategoryId?: string
  lastUsedPrimaryModuleType?:
    | ModuleTypeEnum.ENTITY
    | ModuleTypeEnum.PROJECT
    | ModuleTypeEnum.MEMBER
}

export function isExpenseDraftPreferences(
  prefs: unknown,
): prefs is ExpenseDraftPreferences {
  return typeof prefs === 'object' && prefs !== null
}

export function isAppPreferences(prefs: unknown): prefs is AppPreferences {
  return (
    typeof prefs === 'object' &&
    prefs !== null &&
    ('preferDesktopApp' in prefs ||
      'bennyEnabled' in prefs ||
      'bennyCompanionEnabled' in prefs)
  )
}

// Type for default tab preferences - personal setting per module
export interface DefaultTabPreferences {
  /** The tab ID to open by default when viewing a record (e.g., 'overview', 'tasks', 'contacts') */
  defaultTab: string
  /** Index signature for compatibility */
  [key: string]: unknown
}

export function isDefaultTabPreferences(
  prefs: unknown,
): prefs is DefaultTabPreferences {
  return typeof prefs === 'object' && prefs !== null && 'defaultTab' in prefs
}
